import React, { useMemo, useState } from 'react';
import './FinancialReviewCard.css';

const CLASS_LABELS = {
  recurring: 'Recurring',
  one_time: 'One-time',
  volume_driven: 'Volume-driven',
  anomalous: 'Anomalous',
  unresolved: 'Needs evidence',
};

const currency = value => Number(value || 0).toLocaleString(undefined, {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

function RoomsExpenseAudit({ artifact }) {
  const data = artifact.data || {};
  const rows = data.classifications || [];
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, 6);
  const savings = rows.reduce((sum, row) => sum + Number(row.annualizedSavingsOpportunity || 0), 0);
  const r = data.reconciliation || {};

  return (
    <div className="fr-card">
      <div className="fr-summary">
        <div><span>Rooms expense</span><strong>{currency(r.departmentActual)}</strong></div>
        <div><span>Budget</span><strong>{currency(r.departmentBudget)}</strong></div>
        <div><span>Recurring opportunity</span><strong className={savings > 0 ? 'is-good' : ''}>{currency(savings)}</strong></div>
      </div>
      <div className="fr-tieout">
        <span className="fr-mark" />
        <span>{rows.length} unfavorable accounts classified</span>
        <strong>{currency(r.flaggedUnfavorableTotal)} tied to GL detail</strong>
      </div>
      <div className="fr-list">
        {visible.map(row => (
          <div className="fr-row" key={row.chartOfAccountId}>
            <div className="fr-name">
              <strong>{row.accountName}</strong>
              <span>{row.vendor || row.department}</span>
            </div>
            <span className={`fr-tag fr-tag--${row.classification}`}>{CLASS_LABELS[row.classification] || row.classification}</span>
            <strong className="fr-value">{currency(row.unfavorableVariance)}</strong>
            <span className="fr-reason">{row.reason}</span>
          </div>
        ))}
      </div>
      {rows.length > 6 && (
        <button className="fr-more" type="button" onClick={() => setShowAll(v => !v)}>
          {showAll ? 'Show fewer accounts' : `Show ${rows.length - 6} more accounts`}
        </button>
      )}
      <div className="fr-foot">One-time, volume-supported, anomalous, and unresolved items are excluded from recurring savings.</div>
    </div>
  );
}

function MonthlyCloseReview({ artifact }) {
  const data = artifact.data || {};
  const comparison = data.comparison || {};
  const rows = data.accruals || [];
  const total = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const coverage = data.coverage || {};
  const metrics = useMemo(() => [
    ['Revenue', comparison.current?.revenue, comparison.change?.revenue],
    ['Expense', comparison.current?.expense, comparison.change?.expense],
    ['Net income', comparison.current?.netIncome, comparison.change?.netIncome],
  ], [comparison]);

  return (
    <div className="fr-card">
      <div className="fr-coverage">
        <span className="fr-mark" />
        <div><strong>Coverage aligned</strong><span>{coverage.current?.startDate}–{coverage.current?.endDate} vs {coverage.prior?.startDate}–{coverage.prior?.endDate}</span></div>
      </div>
      <div className="fr-summary">
        {metrics.map(([label, current, change]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{currency(current)}</strong>
            <small className={Number(change) < 0 ? 'is-down' : 'is-up'}>{Number(change) > 0 ? '+' : ''}{currency(change)} vs aligned prior</small>
          </div>
        ))}
      </div>
      <div className="fr-tieout">
        <span>{rows.length ? `${rows.length} cadence-backed accrual${rows.length === 1 ? '' : 's'}` : 'No accrual cleared every gate'}</span>
        <strong>{currency(total)} · review only</strong>
      </div>
      {rows.length > 0 && <div className="fr-list">
        {rows.map(row => (
          <div className="fr-row fr-row--close" key={`${row.chartOfAccountId}:${row.vendor}`}>
            <div className="fr-name"><strong>{row.accountName}</strong><span>{row.department} · {row.vendor}</span></div>
            <span className="fr-tag">{row.monthsAppearing}/6 months</span>
            <strong className="fr-value">{currency(row.amount)}</strong>
            <span className="fr-reason">Reverse {row.reversalTiming}; no entry is posted automatically.</span>
          </div>
        ))}
      </div>}
      <div className="fr-foot">{data.excluded?.length || 0} irregular, quarterly/off-cadence, already-posted, not-yet-due, or immaterial items were excluded.</div>
    </div>
  );
}

export default function FinancialReviewCard({ artifact }) {
  if (artifact.type === 'rooms_expense_audit') return <RoomsExpenseAudit artifact={artifact} />;
  return <MonthlyCloseReview artifact={artifact} />;
}

